import { useGetAllAssignedAssessmentQuery } from "@/app/features/recruitment/job-applicants/job-applicants.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import GiveMark from "@/pages/Admin/Recruitment/Evaluation/_components/EvAssessment/GiveMark";
import OfferLetterDrawer from "@/pages/Admin/Recruitment/Hiring/_components/OfferLetterDrawer";
import { Button, Flex, type TableProps } from "antd";
import { useAppDiscloser, useAppFilters } from "../../../../../hooks";
import SendOfferLetter from "../../Interviews/_components/SendOfferLetter";
import { formatDate, formatTo12Hour, normalizeColumnValue } from "@/utils";

const ASSESS_STATUS_CONFIG = [
  { label: "No-Show", value: "no_show", className: "status status-error" },
  { label: "Failed", value: "failed", className: "status status-error" },
  { label: "Pending", value: "pending", className: "status status-yellow" },
  {
    label: "Submitted",
    value: "submitted",
    className: "status status-success",
  },
  {
    label: "Passed",
    value: "passed",
    className: "status status-success",
  },
  {
    label: "In Progress",
    value: "in_progress",
    className: "status status-yellow",
  },
];

export default function AssessmentsScores() {
  const { page, limit, filters, keyword, handlePagination } = useAppFilters(
    FILTER_KEYS.ASSESS_SCORES,
  );

  const {
    data: assessmentsData,
    isLoading,
    isFetching,
  } = useGetAllAssignedAssessmentQuery({
    params: {
      page,
      limit,
      search: keyword || filters?.search,
      ...filters,
    },
  });

  const [config, handleOpen, handleClose] = useAppDiscloser<
    "MARKS" | "OFFER" | "VIEW_OFFER"
  >(DISCLOSER_KEYS.ASSESS_SCORES);

  const columns: TableProps<any>["columns"] = [
    {
      title: "Applicant",
      dataIndex: "applicant_name",
      // align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },

    {
      title: "Reviewer",
      dataIndex: "evaluator_name",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Job Applied",
      dataIndex: "job_title",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Assessment Name",
      dataIndex: "assessment_name",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Deadlines",
      dataIndex: "deadline_date",
      align: "center",
      render: (val: string, record: any) => {
        const date = formatDate(val);
        const time = formatTo12Hour(record.deadline_time);

        return (
          <span>
            {date}
            {" - "}
            {time}
          </span>
        );
      },
    },
    {
      title: "Marks",
      dataIndex: "score",
      align: "center",
      render: (val: number, record: any) => {
        const percentage =
          record?.total_mark && record?.total_mark > 0
            ? ((val / record.total_mark) * 100).toFixed(0)
            : null;

        return (
          <span>
            {val !== null && val !== undefined ? (
              <Button
                type="default"
                className="text-[#00C6AE] border-none !bg-transparent shadow-none
                underline underline-offset-4 decoration-[#00C6AE] p-0"
                onClick={() =>
                  handleOpen({
                    type: "MARKS",
                    data: {
                      id: record?.id,
                      applicant_name: record?.applicant_name,
                    },
                  })
                }
              >
                {percentage ? `${percentage}%` : "0%"}
              </Button>
            ) : (
              <div>N/A</div>
            )}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "assessment_status",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          shouldCapitalize={false}
          config={ASSESS_STATUS_CONFIG}
        />
      ),
    },

    {
      title: "Actions",
      dataIndex: "assessment_status",
      align: "end",
      render: (val, record) => (
        <Flex gap={12} justify="end" align="center">
          {/* See Offer Letter — only shown after offer has been sent */}
          {record?.is_offer_letter_send && (
            <Button
              type="default"
              className="text-theme-primary border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-theme-primary p-0"
              onClick={() =>
                handleOpen({
                  type: "VIEW_OFFER",
                  data: { offer_letter_id: record?.offer_letter_id },
                })
              }
            >
              View Offer Letter
            </Button>
          )}

          {/* Send Offer Letter — active only when passed AND not yet sent */}
          {val === "passed" && !record?.is_offer_letter_send ? (
            <Button
              type="default"
              className="text-[#00C6AE] border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-[#00C6AE] p-0"
              onClick={() => handleOpen({ type: "OFFER", data: record })}
            >
              Send Offer Letter
            </Button>
          ) : (
            !record?.is_offer_letter_send && (
              <Button
                disabled
                type="default"
                className="!text-[#979797] border-none !bg-transparent
                shadow-none underline underline-offset-4 decoration-[#979797] p-0"
              >
                Send Offer Letter
              </Button>
            )
          )}
        </Flex>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        tableRootClassName="border-0 p-0"
        dataSource={assessmentsData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Scores"
        loading={isLoading || isFetching}
        total={assessmentsData?.data?.meta?.total || 0}
      />

      <GiveMark
        isView
        id={config.data?.id}
        applicantName={config.data?.applicant_name}
        open={config.open && config.type === "MARKS"}
        handleClose={handleClose}
      />
      <SendOfferLetter
        data={config.data}
        open={config.open && config.type === "OFFER"}
        handleClose={handleClose}
      />
      <OfferLetterDrawer
        open={config.open && config.type === "VIEW_OFFER"}
        onClose={handleClose}
        offerLetterId={config.data?.offer_letter_id ?? null}
      />
    </div>
  );
}
